import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Navigate, useNavigate } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';

import { orderLookupV1CheckMutation } from '@/api/generated/@tanstack/react-query.gen';
import { useOrderLookupStartMutation } from '@/api/hooks/mutations/use-order-lookup-start-mutation';
import { useFeatureFlagState } from '@/hooks/use-feature-flag-state';
import { LOGGER_ACTIONS, logger } from '@/lib/logger';

import { OrderLookupHandoff } from './components/OrderLookupHandoff';
import {
  type OrderLookupIdentityFormData,
  OrderLookupIdentityStep,
} from './components/OrderLookupIdentityStep';
import { OrderLookupVerifyStep } from './components/OrderLookupVerifyStep';

const OrderLookupStep = {
  IDENTITY: 'identity',
  VERIFY: 'verify',
  HANDOFF: 'handoff',
} as const;

type OrderLookupStepValue = (typeof OrderLookupStep)[keyof typeof OrderLookupStep];

// `loading` shows a loader rather than redirecting; bouncing on `disabled` before
// the flag resolves would eject a legitimately-enabled user on a cold deep-link.
export function OrderLookup() {
  const flagState = useFeatureFlagState('self-serve-order-lookup');

  if (flagState === 'loading') {
    return (
      <div className="flex items-center justify-center py-12" role="status" aria-busy="true">
        <Loader2 className="size-8 animate-spin text-hydrangea-500" aria-hidden="true" />
        <span className="sr-only">Loading...</span>
      </div>
    );
  }

  if (flagState === 'disabled') {
    return <Navigate to="/" replace />;
  }

  return <OrderLookupFlow />;
}

function OrderLookupFlow() {
  const navigate = useNavigate();
  const startMutation = useOrderLookupStartMutation();
  // credentials: 'include' so the browser stores the httpOnly handoff cookie the API sets.
  const checkMutation = useMutation(orderLookupV1CheckMutation({ credentials: 'include' }));

  const [step, setStep] = useState<OrderLookupStepValue>(OrderLookupStep.IDENTITY);
  const [identity, setIdentity] = useState<OrderLookupIdentityFormData | null>(null);
  const [handle, setHandle] = useState<string | null>(null);
  const [publicId, setPublicId] = useState<string | null>(null);
  const [startError, setStartError] = useState(false);
  const [resendError, setResendError] = useState(false);
  const [verifyError, setVerifyError] = useState<string | undefined>(undefined);

  const handleIdentitySubmit = async (values: OrderLookupIdentityFormData) => {
    logger.action(LOGGER_ACTIONS.ORDER_LOOKUP.IDENTITY_SUBMITTED);

    try {
      const response = await startMutation.mutateAsync({
        firstName: values.firstName,
        lastName: values.lastName,
        dob: values.dateOfBirth,
        recipient: values.recipient,
      });

      if (!response.handle) {
        throw new Error('Order lookup start returned no handle');
      }

      logger.action(LOGGER_ACTIONS.ORDER_LOOKUP.OTP_SENT);
      setIdentity(values);
      setHandle(response.handle);
      setStartError(false);
      setResendError(false);
      setVerifyError(undefined);
      setStep(OrderLookupStep.VERIFY);
    } catch (error) {
      logger.error('Order lookup start failed', error);
      logger.action(LOGGER_ACTIONS.ORDER_LOOKUP.START_FAILED);
      setStartError(true);
    } finally {
      // The initial send isn't a resend, so it shouldn't consume the cooldown:
      // clear it so the first resend on the verify step starts a fresh countdown.
      startMutation.reset();
    }
  };

  const handleResend = async () => {
    if (!identity || !startMutation.canResend) return;

    logger.action(LOGGER_ACTIONS.ORDER_LOOKUP.OTP_RESENT);

    try {
      const response = await startMutation.mutateAsync({
        firstName: identity.firstName,
        lastName: identity.lastName,
        dob: identity.dateOfBirth,
        recipient: identity.recipient,
      });

      if (!response.handle) {
        throw new Error('Order lookup resend returned no handle');
      }

      setHandle(response.handle);
      setResendError(false);
      setVerifyError(undefined);
    } catch (error) {
      logger.error('Order lookup resend failed', error);
      setResendError(true);
    }
  };

  const handleVerifySubmit = async (code: string) => {
    if (!handle) return;

    setVerifyError(undefined);

    try {
      const response = await checkMutation.mutateAsync({ body: { handle, code } });

      if (!response.public_id) {
        throw new Error('Order lookup check returned no public_id');
      }

      logger.action(LOGGER_ACTIONS.ORDER_LOOKUP.OTP_VERIFIED);
      setPublicId(response.public_id);
      setStep(OrderLookupStep.HANDOFF);
    } catch (error) {
      logger.error('Order lookup check failed', error);
      logger.action(LOGGER_ACTIONS.ORDER_LOOKUP.OTP_FAILED);
      setVerifyError('That code didn’t match. Please check the code and try again.');
    }
  };

  if (step === OrderLookupStep.HANDOFF && publicId) {
    return <OrderLookupHandoff firstName={identity?.firstName ?? ''} publicId={publicId} />;
  }

  if (step === OrderLookupStep.VERIFY) {
    return (
      <OrderLookupVerifyStep
        onBack={() => setStep(OrderLookupStep.IDENTITY)}
        onSubmit={handleVerifySubmit}
        onResend={handleResend}
        isVerifying={checkMutation.isPending}
        isResending={startMutation.isPending}
        canResend={startMutation.canResend}
        remainingMs={startMutation.remainingMs}
        resendError={resendError}
        error={verifyError}
      />
    );
  }

  return (
    <OrderLookupIdentityStep
      defaultValues={identity ?? undefined}
      onBack={() => navigate({ to: '/', replace: true })}
      onSubmit={handleIdentitySubmit}
      isPending={startMutation.isPending}
      error={startError ? 'We couldn’t start your order lookup. Please try again.' : undefined}
    />
  );
}
